import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { submitDemoRequest } from '../services/demoRequestService';

const FRENCH_NUMBER = (n: number) => n.toLocaleString('fr-FR');
const FRENCH_DECIMAL = (n: number) =>
  (Math.round(n * 10) / 10).toString().replace('.', ',');

const ZELLIGE_BG = `radial-gradient(circle at 25% 25%, #1A4332 1.2px, transparent 1.5px),
  radial-gradient(circle at 75% 75%, #1A4332 1.2px, transparent 1.5px),
  radial-gradient(circle at 50% 50%, #B8593E 0.8px, transparent 1px)`;

const ZELLIGE_BG_LIGHT = `radial-gradient(circle at 25% 25%, #FBF8F2 1.2px, transparent 1.5px),
  radial-gradient(circle at 75% 75%, #FBF8F2 1.2px, transparent 1.5px),
  radial-gradient(circle at 50% 50%, #B8593E 0.8px, transparent 1px)`;

const academicFeatures = [
  "Dossiers étudiants complets, avec CNIE, scolarité, parents et contacts d'urgence",
  'Inscriptions, ré-inscriptions et changements de filière',
  'Gestion des filières, programmes, semestres, modules et éléments',
  'Emplois du temps et planning des cours',
  'Planning des examens et planning des rattrapages',
  'Présences et absences, étudiants comme professeurs',
  'Notes et évaluations multi-composantes (CC, partiel, final, projet)',
  'Délibérations de jury et validation des notes',
  'Bulletins et relevés de notes',
  'Exports E-DEP automatisés et conformes',
  'Documents administratifs : certificats, attestations, conventions',
  'Évaluation des cours par les étudiants',
  'Suivi et engagement des professeurs vacataires et permanents',
  'Espace parent pour le suivi de la scolarité',
];

const financeFeatures = [
  'Frais de scolarité, multi-filière et multi-année',
  'Frais annexes : livres, transport, restauration, etc.',
  'Échéanciers de paiement personnalisés',
  'Encaissements multi-canaux : espèces, virement et chèque',
  'Remises, bourses et exonérations paramétrables',
  'Relances automatiques aux familles',
  'Suspension financière paramétrable et levée automatique',
  'Comptes étudiants, soldes et historique complet',
  'Reçus automatiques',
  'Paie du personnel permanent',
  'Gestion des vacataires : taux horaires, heures et lots de paiement',
  "Comptes bancaires de l'institution et rapprochement",
  'Tableaux de bord financiers en temps réel',
];

const benefits: Array<{ n: string; title: string; body: string; accent?: boolean }> = [
  {
    n: '01',
    title: 'Préparation E-DEP simplifiée',
    body: "Vos données sont déjà au bon format. La transmission au ministère devient une formalité de quelques clics, plus une opération de plusieurs semaines.",
  },
  {
    n: '02',
    title: 'Centralisation des données',
    body: "Une seule source de vérité pour les étudiants, les notes, les paiements et le personnel. Fini les Excel parallèles, les dossiers papier dupliqués, les versions qui ne se parlent pas.",
  },
  {
    n: '03',
    title: 'Suppression des tâches redondantes',
    body: "Attestations, certificats, bulletins, relevés, relances de paiement, distribution des supports de cours, envoi des plannings : tout se génère et se diffuse automatiquement. Vos équipes ne ressaisissent plus jamais les mêmes informations.",
  },
  {
    n: '04',
    title: "Taux d'erreur en chute de 95%",
    body: "Plus de double saisie, plus de notes recopiées à la main, plus de paiements oubliés. La donnée est saisie une seule fois et propagée automatiquement, ce qui élimine la quasi totalité des erreurs administratives.",
    accent: true,
  },
  {
    n: '05',
    title: 'Pilotage par la donnée',
    body: "Tableaux de bord en temps réel, recouvrement, présence, performance par filière. Vous prenez vos décisions sur des chiffres, pas sur des intuitions.",
  },
  {
    n: '06',
    title: 'Communication structurée, fin du chaos WhatsApp',
    body: "Les notes, plannings, supports de cours, paiements et annonces ne se gèrent plus dans des dizaines de groupes WhatsApp éparpillés. Tout est centralisé, traçable, et accessible à chaque rôle au bon endroit.",
    accent: true,
  },
  {
    n: '07',
    title: 'Conformité réglementaire intégrée',
    body: "Loi 09-08, exigences CNDP, journal d'audit, sauvegardes, droit à la portabilité. Tout est intégré, à jour, et sans démarche supplémentaire.",
  },
];

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  institution: string;
  role: string;
  studentRange: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  institution: '',
  role: '',
  studentRange: '',
  message: '',
};

export const HomePage: React.FC = () => {
  // Restore the page-level title because the SPA shell otherwise carries
  // the institution-shell title across navigation.
  useEffect(() => {
    const previous = document.title;
    document.title = 'NEXEDU, plateforme de gestion universitaire conçue au Maroc';
    return () => {
      document.title = previous;
    };
  }, []);

  const [students, setStudents] = useState(300);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showHypotheses, setShowHypotheses] = useState(false);

  const simulator = useMemo(() => {
    const hoursPerStudent = 10;
    const fixedEdepHours = 25;
    const totalHours = Math.round(students * hoursPerStudent + fixedEdepHours);
    const hourlyRate = 30;
    const mad = totalHours * hourlyRate;
    const fte = totalHours / 1800;
    return { totalHours, mad, fte };
  }, [students]);

  const updateForm = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      await submitDemoRequest({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        institution: form.institution.trim(),
        role: form.role.trim() || undefined,
        studentRange: form.studentRange.trim() || undefined,
        message: form.message.trim() || undefined,
      });
      setSubmitState('success');
      setForm(INITIAL_FORM);
    } catch (err) {
      setSubmitState('error');
      setSubmitError(err instanceof Error ? err.message : "Une erreur est survenue, réessayez plus tard.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-cream-100 text-ink-800 font-sans antialiased">
      {/* NAV */}
      <header className="sticky top-0 z-50 bg-cream-100/85 backdrop-blur-md border-b border-cream-300/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="#" className="font-display font-extrabold text-3xl text-forest-700 tracking-tight">NEXEDU</a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-ink-600">
            <a href="#plateforme" className="hover:text-forest-700 transition">Plateforme</a>
            <a href="#tarifs" className="hover:text-forest-700 transition">Tarifs</a>
            <a href="#a-propos" className="hover:text-forest-700 transition">À propos</a>
            <a href="#contact" className="hover:text-forest-700 transition">Contact</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden sm:inline-block text-sm font-medium text-ink-600 hover:text-forest-700 transition">Connexion</Link>
            <a href="#demo" className="bg-forest-700 hover:bg-forest-800 text-white inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-md transition">
              Demander une démo
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-cream-100">
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{ backgroundImage: ZELLIGE_BG, backgroundSize: '32px 32px, 32px 32px, 16px 16px', backgroundPosition: '0 0, 16px 16px, 8px 8px' }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.2em] text-clay-600 font-semibold mb-6">
                Plateforme de gestion universitaire, conçue au Maroc
              </div>
              <h1 className="font-display font-extrabold text-5xl lg:text-6xl xl:text-7xl text-ink-800 leading-[1.05] mb-6 tracking-tight">
                De l'admission à la diplomation, sur une seule plateforme.
              </h1>
              <p className="text-lg text-ink-600 leading-relaxed mb-8 max-w-xl">
                NEXEDU rassemble la gestion académique et financière de votre établissement, université ou école supérieure, dans un système conçu pour le Maroc.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="#demo" className="bg-forest-700 hover:bg-forest-800 text-white inline-flex items-center justify-center gap-2 font-semibold px-7 py-3.5 rounded-md transition">
                  Demander une démo
                </a>
                <a href="#plateforme" className="bg-white border-2 border-cream-300 hover:border-forest-700 text-forest-700 inline-flex items-center justify-center gap-2 font-semibold px-7 py-3.5 rounded-md transition">
                  Découvrir la plateforme
                </a>
              </div>
            </div>

            <DashboardMockup />
          </div>
        </div>
      </section>

      {/* NOTRE CONVICTION */}
      <section id="a-propos" className="bg-forest-700 text-cream-100 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.10]" style={{ backgroundImage: ZELLIGE_BG_LIGHT, backgroundSize: '32px 32px, 32px 32px, 16px 16px', backgroundPosition: '0 0, 16px 16px, 8px 8px' }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.2em] text-clay-200 font-semibold mb-4">Notre conviction</div>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl leading-[1.1] text-white tracking-tight">
                Pourquoi NEXEDU existe.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p className="font-display text-3xl lg:text-4xl leading-[1.25] text-white mb-10 tracking-tight">
                "Une scolarité bien pilotée, c'est avant tout des équipes libérées de la paperasse, pour qu'elles puissent faire leur vrai métier : éduquer."
              </p>
              <p className="text-lg text-cream-200 leading-relaxed mb-6 max-w-2xl">
                NEXEDU est né d'un constat simple. Les universités et écoles supérieures du Maroc passent un temps considérable sur des tâches administratives répétitives, à saisir les mêmes données dans plusieurs outils, à courir après des certificats, à empiler des fichiers Excel.
              </p>
              <p className="text-lg text-cream-200 leading-relaxed max-w-2xl">
                Nous construisons une plateforme locale, alignée sur la réglementation marocaine et pensée pour les vrais usages du terrain, pour que vos équipes pédagogiques et administratives retrouvent ce qui fait le sens de leur métier : accompagner les étudiants.
              </p>
              <div className="mt-10 flex items-center gap-4 pt-8 border-t border-cream-100/15">
                <div className="w-12 h-12 rounded-full bg-clay-500" />
                <div>
                  <div className="font-display font-bold text-white">Tahar Harkat</div>
                  <div className="text-sm text-cream-200">Fondateur, NEXEDU</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-20 pt-12 border-t border-cream-100/15">
            <div>
              <div className="font-display font-extrabold text-5xl text-white mb-2 tracking-tight">100%</div>
              <div className="text-sm text-cream-200 font-medium">Conçu et hébergé au Maroc</div>
            </div>
            <div>
              <div className="font-display font-extrabold text-5xl text-white mb-2 tracking-tight">2</div>
              <div className="text-sm text-cream-200 font-medium">Modules intégrés, activables séparément</div>
            </div>
            <div>
              <div className="font-display font-extrabold text-5xl text-white mb-2 tracking-tight">09-08</div>
              <div className="text-sm text-cream-200 font-medium">Conforme à la loi 09-08 et aux exigences CNDP</div>
            </div>
          </div>
        </div>
      </section>

      {/* LA PLATEFORME */}
      <section id="plateforme" className="bg-cream-100 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-20">
            <div className="text-xs uppercase tracking-[0.2em] text-clay-600 font-semibold mb-4">La plateforme</div>
            <h2 className="font-display font-extrabold text-4xl lg:text-6xl text-ink-800 leading-[1.05] mb-6 tracking-tight">
              Tout l'établissement, sur un seul système.
            </h2>
            <p className="text-lg text-ink-600 leading-relaxed">
              De l'admission jusqu'à la diplomation, NEXEDU couvre toutes les étapes administratives, pédagogiques et financières de la vie d'un étudiant. Deux modules indépendants, à activer ensemble ou séparément selon vos besoins.
            </p>
          </div>

          <ModuleCard label="Module 01" title="Académique" description="Du dossier d'admission à la délivrance du diplôme. Une seule interface pour vos équipes pédagogiques et administratives." features={academicFeatures} />

          <div className="h-8" />

          <ModuleCard label="Module 02" title="Finance" description="Pilotage financier complet, conforme aux normes marocaines. De la facturation des étudiants à la paie du personnel." features={financeFeatures} />

          <p className="text-center text-sm text-ink-500 mt-10 max-w-xl mx-auto">
            Les deux modules sont indépendants. Activez l'un, l'autre, ou les deux selon les besoins de votre établissement.
          </p>
        </div>
      </section>

      {/* BÉNÉFICES + SIMULATEUR */}
      <section id="benefices" className="bg-cream-100 py-24 lg:py-32 border-t border-cream-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-clay-600 font-semibold mb-4">Bénéfices concrets</div>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-ink-800 leading-[1.1] mb-6 tracking-tight">
                Ce que NEXEDU change dans votre établissement.
              </h2>
              <p className="text-ink-600 leading-relaxed mb-10">
                Au delà des fonctionnalités, ce sont les usages quotidiens qui changent. Voici ce que vous gagnez concrètement, mois après mois.
              </p>
              <div className="space-y-6">
                {benefits.map((b) => (
                  <div key={b.n} className="flex gap-4">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-md text-white font-display font-extrabold flex items-center justify-center text-sm ${b.accent ? 'bg-clay-600' : 'bg-forest-700'}`}>{b.n}</div>
                    <div>
                      <h3 className="font-display font-bold text-ink-800 text-lg mb-1 tracking-tight">{b.title}</h3>
                      <p className="text-sm text-ink-600 leading-relaxed">{b.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:sticky lg:top-28">
              <div className="bg-white rounded-3xl border border-cream-300 p-8 lg:p-10 shadow-sm">
                <div className="text-xs uppercase tracking-[0.2em] text-clay-600 font-semibold mb-4">Estimez vos gains</div>
                <h3 className="font-display font-extrabold text-2xl lg:text-3xl text-ink-800 leading-tight mb-6 tracking-tight">
                  Combien votre établissement peut économiser par an.
                </h3>

                <div className="mb-8">
                  <div className="flex items-baseline justify-between mb-3">
                    <label htmlFor="sim-students" className="text-sm font-semibold text-ink-700">Nombre d'étudiants</label>
                    <div className="font-display font-extrabold text-2xl text-forest-700 tracking-tight">{FRENCH_NUMBER(students)}</div>
                  </div>
                  <input
                    id="sim-students"
                    type="range"
                    min={50}
                    max={3000}
                    step={25}
                    value={students}
                    onChange={(e) => setStudents(parseInt(e.target.value, 10))}
                    className="sim-range w-full"
                    style={{ WebkitAppearance: 'none', appearance: 'none', height: 6, background: '#E5DCC8', borderRadius: 9999, outline: 'none' }}
                  />
                  <div className="flex justify-between text-[11px] text-ink-500 mt-2">
                    <span>50</span><span>1 500</span><span>3 000</span>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <SimStat label="Heures économisées par an" value={`${FRENCH_NUMBER(simulator.totalHours)}`} unit="heures" sub="Sur l'ensemble du personnel administratif, toutes tâches confondues." />
                  <SimStat label="Économies estimées par an" value={FRENCH_NUMBER(simulator.mad)} unit="MAD" sub="Sur la base d'un coût horaire moyen de 30 MAD pour le personnel administratif." accent />
                  <SimStat label="Personnel libéré, équivalent temps plein" value={FRENCH_DECIMAL(simulator.fte)} unit="personnes" sub="Du temps redirigé de la paperasse vers ce qui compte vraiment, l'éducation." />
                </div>

                <div className="bg-forest-700 text-white rounded-xl p-5 mb-6">
                  <div className="text-xs uppercase tracking-wide text-clay-200 font-semibold mb-3">Et au delà des chiffres</div>
                  <div className="space-y-2.5 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-clay-200 mt-0.5">▸</span>
                      <div><strong className="text-white">Taux d'erreur administratif réduit de 95%</strong>, parce qu'une donnée saisie une seule fois ne se contredit jamais.</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-clay-200 mt-0.5">▸</span>
                      <div><strong className="text-white">Fin de la coordination par WhatsApp</strong>, remplacée par une communication structurée, traçable et accessible.</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-clay-200 mt-0.5">▸</span>
                      <div><strong className="text-white">Qualité d'enseignement renforcée</strong>, parce que chaque heure libérée est une heure rendue à l'accompagnement étudiant.</div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <button type="button" onClick={() => setShowHypotheses((v) => !v)} className="text-xs text-ink-500 hover:text-ink-700 font-semibold">
                    {showHypotheses ? 'Masquer le détail ▴' : 'Voir le détail des hypothèses ▾'}
                  </button>
                  {showHypotheses && (
                    <div className="text-[11px] text-ink-500 leading-relaxed mt-3 space-y-1.5">
                      <div>Estimation basée sur 10 heures économisées par étudiant et par an, réparties ainsi :</div>
                      <ul className="list-disc list-inside space-y-0.5 ml-2">
                        <li>Génération automatique des attestations et certificats : 1,5 h</li>
                        <li>Saisie et publication des notes, bulletins, relevés : 2 h</li>
                        <li>Suivi et relance des paiements : 1 h</li>
                        <li>Distribution des supports de cours : 1,5 h</li>
                        <li>Envoi des plannings et notifications de changement : 0,5 h</li>
                        <li>Saisie évitée par centralisation, double entrée éliminée : 1 h</li>
                        <li>Communication parent et étudiant centralisée : 1 h</li>
                        <li>Réclamations et demandes étudiantes : 1 h</li>
                        <li>Coordination remplaçant les groupes WhatsApp : 0,5 h</li>
                      </ul>
                      <div>Plus 25 heures fixes par an pour la préparation des exports E-DEP. Coût horaire moyen pris à 30 MAD pour le personnel administratif. Les gains réels dépendent de votre organisation actuelle.</div>
                    </div>
                  )}
                </div>

                <a href="#demo" className="bg-forest-700 hover:bg-forest-800 text-white block text-center font-semibold px-6 py-3.5 rounded-md transition">
                  Demander une estimation personnalisée
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="bg-cream-100 py-24 lg:py-32 border-t border-cream-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-28">
              <div className="text-xs uppercase tracking-[0.2em] text-clay-600 font-semibold mb-4">Démarrage</div>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-ink-800 leading-[1.1] mb-6 tracking-tight">
                De zéro à go-live, en 4 à 8 semaines.
              </h2>
              <p className="text-ink-600 leading-relaxed">
                Notre équipe vous accompagne à chaque étape. Du premier appel découverte jusqu'à la formation des équipes et la mise en production.
              </p>
            </div>
            <div className="lg:col-span-8 space-y-4">
              <Step n="1" title="Appel découverte" body="Nous comprenons votre établissement, vos campus, vos filières, vos contraintes. Vous repartez avec un plan d'implémentation personnalisé et un devis clair." />
              <Step n="2" title="Configuration et migration" body="Nous configurons votre instance, importons vos données existantes et paramétrons vos campus, filières, grilles de notes et rôles utilisateurs. L'approche de migration est définie ensemble en amont, selon vos systèmes actuels." />
              <Step n="3" title="Formation et go-live" body="Sessions de formation par rôle (DG, scolarité, finance, professeurs, étudiants), documentation française complète, et accompagnement humain de votre équipe pendant les premières semaines de mise en production." />
              <div className="bg-forest-700 text-white rounded-2xl p-7 flex gap-5">
                <div className="flex-shrink-0 w-10 h-10 rounded-md bg-clay-600 text-white font-display font-extrabold flex items-center justify-center">★</div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-2 tracking-tight">Support et évolution continue</h3>
                  <p className="text-cream-200 leading-relaxed">Nouvelles fonctionnalités intégrées au fil des publications. Vos retours façonnent le produit, en continu.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TARIFS */}
      <section id="tarifs" className="bg-cream-100 py-24 lg:py-32 border-t border-cream-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-clay-600 font-semibold mb-4">Tarification</div>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-ink-800 leading-tight tracking-tight">
              Une offre construite pour votre établissement.
            </h2>
          </div>
          <div className="max-w-2xl mx-auto bg-white rounded-3xl border border-cream-300 p-10 lg:p-14 shadow-sm text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-clay-600 font-semibold mb-4">Sur devis</div>
            <h3 className="font-display font-extrabold text-3xl lg:text-4xl text-ink-800 leading-tight mb-4 tracking-tight">
              Tarification adaptée à votre situation.
            </h3>
            <p className="text-lg text-ink-600 leading-relaxed mb-8 max-w-lg mx-auto">
              Chaque établissement est unique. Nombre d'étudiants, campus, modules activés, périmètre de migration : nous construisons une offre ajustée à vos besoins réels.
            </p>
            <a href="#demo" className="bg-forest-700 hover:bg-forest-800 text-white inline-flex items-center justify-center gap-2 font-semibold px-8 py-4 rounded-md transition">
              Demander un devis
            </a>
            <p className="text-sm text-ink-500 mt-8">
              Réponse personnalisée sous 24 heures, sans engagement.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto text-center">
            <div>
              <div className="font-display font-bold text-ink-800 mb-1">Accès web sécurisé</div>
              <div className="text-sm text-ink-500">Aucun matériel à installer, depuis n'importe quel navigateur</div>
            </div>
            <div>
              <div className="font-display font-bold text-ink-800 mb-1">Mises à jour incluses</div>
              <div className="text-sm text-ink-500">Nouvelles fonctionnalités intégrées au fil des publications</div>
            </div>
            <div>
              <div className="font-display font-bold text-ink-800 mb-1">Support en français</div>
              <div className="text-sm text-ink-500">Réponses rapides, sans intermédiaire</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-cream-100 py-24 lg:py-32 border-t border-cream-300">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-clay-600 font-semibold mb-4">Questions fréquentes</div>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-ink-800 tracking-tight">
              Vous avez une question, on a une réponse.
            </h2>
          </div>
          <div className="space-y-3">
            <FaqItem q="Combien de temps prend la mise en place ?">
              Pour un établissement standard avec un campus et quelques filières, comptez 4 à 6 semaines entre la signature et le go-live.
            </FaqItem>
            <FaqItem q="Combien ça coûte ?">
              La tarification est adaptée à la taille de votre établissement, au nombre de modules activés et au périmètre de migration. Nous construisons une offre personnalisée après un appel découverte de 30 minutes. <a href="#demo" className="text-forest-700 underline underline-offset-2">Demandez un devis</a> pour démarrer la conversation.
            </FaqItem>
            <FaqItem q="Où sont hébergées nos données, et qui y a accès ?">
              Toutes les données sont hébergées au Maroc, sur une infrastructure conforme à la loi 09-08 et aux exigences de la CNDP. Vous restez pleinement propriétaire de vos données. Aucun tiers n'y a accès en dehors de votre équipe et de notre support, et uniquement sur autorisation explicite.
            </FaqItem>
            <FaqItem q="Pouvez-vous migrer nos données depuis Excel ou un autre logiciel ?">
              Oui. Nous étudions vos systèmes actuels lors de l'appel découverte, puis nous convenons ensemble de l'approche de migration la mieux adaptée : import direct depuis Excel, reprise depuis votre LMS existant, ou ressaisie assistée pour les volumétries plus modestes. Le périmètre est défini à l'avance pour qu'aucune donnée importante ne soit perdue.
            </FaqItem>
            <FaqItem q="Est-ce que ça gère nos exports E-DEP ?">
              Vous pouvez exporter vos données dans un format directement lisible par E-DEP, ce qui rend la transmission de vos données au ministère beaucoup plus simple et plus rapide. Plus besoin de retraiter manuellement vos fichiers avant chaque dépôt.
            </FaqItem>
            <FaqItem q="Que se passe-t-il si nous voulons partir ?">
              Vous récupérez l'intégralité de vos données via un export complet (CSV plus archive ZIP) à tout moment et sans frais. Pas de verrouillage, pas de chantage commercial. Vos données vous appartiennent.
            </FaqItem>
          </div>
        </div>
      </section>

      {/* CTA + DEMO FORM */}
      <section id="demo" className="bg-forest-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.10]" style={{ backgroundImage: ZELLIGE_BG_LIGHT, backgroundSize: '32px 32px, 32px 32px, 16px 16px', backgroundPosition: '0 0, 16px 16px, 8px 8px' }} />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-clay-200 font-semibold mb-4">Demander une démo</div>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl leading-[1.05] mb-6 tracking-tight">
                Prêt à libérer vos équipes ?
              </h2>
              <p className="text-lg text-cream-200 leading-relaxed mb-8">
                30 minutes avec nous, pour vous montrer concrètement ce que NEXEDU peut faire pour votre établissement. Sans engagement, sans pression.
              </p>
              <div className="space-y-3 text-sm text-cream-200">
                <div className="flex items-start gap-3"><span className="text-clay-200 mt-0.5">▸</span><div>Réponse personnalisée sous 24 heures</div></div>
                <div className="flex items-start gap-3"><span className="text-clay-200 mt-0.5">▸</span><div>Démo adaptée à votre fonction et votre établissement</div></div>
                <div className="flex items-start gap-3"><span className="text-clay-200 mt-0.5">▸</span><div>Devis clair et détaillé sous 48 heures</div></div>
              </div>
            </div>

            <div className="lg:col-span-7">
              {submitState === 'success' ? (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 lg:p-10">
                  <div className="text-xs uppercase tracking-[0.2em] text-clay-200 font-semibold mb-3">Demande reçue</div>
                  <h3 className="font-display font-extrabold text-3xl text-white mb-4 tracking-tight">Merci, votre demande nous est parvenue.</h3>
                  <p className="text-cream-200 leading-relaxed mb-6">
                    Vous recevrez un email de confirmation dans quelques minutes. Nous reviendrons vers vous sous 24 heures avec une proposition de créneau.
                  </p>
                  <button onClick={() => setSubmitState('idle')} className="text-cream-200 underline underline-offset-2 text-sm font-semibold hover:text-white">
                    Envoyer une autre demande
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 lg:p-10 space-y-5">
                  {submitState === 'error' && submitError && (
                    <div className="p-4 rounded-md bg-clay-600/20 border border-clay-200/30 text-cream-100 text-sm">
                      {submitError}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field id="f-prenom" label="Prénom *" required value={form.firstName} onChange={updateForm('firstName')} placeholder="Hassan" />
                    <Field id="f-nom" label="Nom *" required value={form.lastName} onChange={updateForm('lastName')} placeholder="Tahiri" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field id="f-email" label="Email professionnel *" required type="email" value={form.email} onChange={updateForm('email')} placeholder="hassan@universite.ma" />
                    <Field id="f-tel" label="Téléphone *" required type="tel" value={form.phone} onChange={updateForm('phone')} placeholder="+212 6 00 00 00 00" />
                  </div>
                  <Field id="f-etab" label="Nom de l'établissement *" required value={form.institution} onChange={updateForm('institution')} placeholder="Université ou école supérieure" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <SelectField id="f-fonction" label="Votre fonction *" required value={form.role} onChange={updateForm('role')}>
                      <option value="">Sélectionner</option>
                      <option value="DG">Directeur Général</option>
                      <option value="DP">Directeur Pédagogique</option>
                      <option value="RS">Responsable scolarité</option>
                      <option value="RF">Responsable financier</option>
                      <option value="RH">Responsable RH</option>
                      <option value="autre">Autre</option>
                    </SelectField>
                    <SelectField id="f-taille" label="Nombre d'étudiants *" required value={form.studentRange} onChange={updateForm('studentRange')}>
                      <option value="">Sélectionner</option>
                      <option value="<100">Moins de 100</option>
                      <option value="100-500">100 à 500</option>
                      <option value="500-1500">500 à 1 500</option>
                      <option value="1500-3000">1 500 à 3 000</option>
                      <option value=">3000">Plus de 3 000</option>
                    </SelectField>
                  </div>

                  <div>
                    <label htmlFor="f-msg" className="block text-xs font-semibold mb-1.5 text-cream-200">Message (optionnel)</label>
                    <textarea id="f-msg" rows={3} value={form.message} onChange={updateForm('message')} className="w-full px-4 py-3 rounded-md bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-white/50 focus:bg-white/10 transition" placeholder="Vos besoins prioritaires, contraintes, calendrier souhaité..." />
                  </div>

                  <button type="submit" disabled={submitting} className="w-full bg-cream-100 text-forest-800 hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed font-semibold px-7 py-4 rounded-md transition">
                    {submitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                  </button>
                  <p className="text-[11px] text-cream-300/80 text-center">
                    En envoyant ce formulaire, vous acceptez d'être contacté par NEXEDU au sujet de votre demande. Vos données sont traitées conformément à la loi 09-08.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contact" className="bg-ink-800 text-cream-200/80">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
            <div className="lg:col-span-2">
              <div className="font-display font-extrabold text-3xl text-white mb-3 tracking-tight">NEXEDU</div>
              <p className="text-sm leading-relaxed mb-6 max-w-sm">
                Plateforme de gestion universitaire et financière, conçue au Maroc pour les universités et écoles supérieures.
              </p>
              <div className="text-sm space-y-1.5">
                <div className="flex items-start gap-2"><span className="text-cream-400 w-4">@</span><a href="mailto:contact@nexedu.ma" className="hover:text-white transition">contact@nexedu.ma</a></div>
                <div className="flex items-start gap-2"><span className="text-cream-400 w-4">@</span><a href="mailto:t.harkat@nexedu.ma" className="hover:text-white transition">t.harkat@nexedu.ma</a></div>
                <div className="flex items-start gap-2"><span className="text-cream-400 w-4">℡</span><a href="tel:+212767121479" className="hover:text-white transition">+212 7 67 12 14 79</a></div>
                <div className="flex items-start gap-2 mt-2">
                  <span className="text-cream-400 w-4">⌂</span>
                  <div>Incubateur Digital Solidaire<br />Angle avenue Assalam, Route de Rabat<br />Salé, Maroc</div>
                </div>
              </div>
            </div>
            <div>
              <div className="font-display font-bold text-white mb-4 text-sm">Plateforme</div>
              <ul className="space-y-2 text-sm">
                <li><a href="#plateforme" className="hover:text-white transition">Module Académique</a></li>
                <li><a href="#plateforme" className="hover:text-white transition">Module Finance</a></li>
                <li><a href="#tarifs" className="hover:text-white transition">Tarifs</a></li>
              </ul>
            </div>
            <div>
              <div className="font-display font-bold text-white mb-4 text-sm">Entreprise</div>
              <ul className="space-y-2 text-sm">
                <li><a href="#a-propos" className="hover:text-white transition">À propos</a></li>
                <li><a href="#demo" className="hover:text-white transition">Demander une démo</a></li>
              </ul>
            </div>
            <div>
              <div className="font-display font-bold text-white mb-4 text-sm">Légal</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/conformite-cndp" className="hover:text-white transition">Conformité CNDP</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-cream-100/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <div>© 2026 NEXEDU. Tous droits réservés.</div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-forest-500" />
              <span>Plateforme opérationnelle</span>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        input[type="range"].sim-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          background: #1A4332;
          border-radius: 9999px;
          border: 4px solid #FBF8F2;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(26,67,50,0.25);
        }
        input[type="range"].sim-range::-moz-range-thumb {
          width: 24px;
          height: 24px;
          background: #1A4332;
          border-radius: 9999px;
          border: 4px solid #FBF8F2;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

// Sub-components

const DashboardMockup: React.FC = () => (
  <div className="lg:col-span-6 relative">
    <div className="absolute inset-0 -translate-x-6 translate-y-6 bg-clay-200/40 rounded-3xl" />
    <div className="relative bg-white border border-cream-300 rounded-2xl shadow-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-cream-200 bg-cream-50">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-clay-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-cream-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-forest-200" />
        </div>
        <div className="ml-3 px-3 py-1 rounded bg-white text-[10px] text-ink-500 font-mono">app.nexedu.ma/institution/.../dg/dashboard</div>
      </div>
      <div className="flex">
        <div className="w-44 bg-cream-50 border-r border-cream-200 p-3 hidden md:block">
          <div className="flex items-center gap-2 mb-5 px-2 py-1">
            <div className="w-7 h-7 rounded bg-forest-700 flex items-center justify-center text-white font-display font-extrabold text-xs">N</div>
            <div className="font-display font-extrabold text-xs text-forest-700">NEXEDU</div>
          </div>
          <nav className="space-y-0.5 text-[11px]">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-forest-700 text-white font-semibold">▦ Tableau de bord</div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded text-ink-600">▤ Établissement</div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded text-ink-600">⚇ Utilisateurs</div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded text-ink-600">📚 Pédagogie</div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded text-ink-600">🗓 Calendrier</div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded text-ink-600">✓ Notes</div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded text-ink-600">$ Finance</div>
          </nav>
        </div>
        <div className="flex-1 p-4 bg-white">
          <div className="bg-forest-700 rounded-lg p-3 mb-3 text-white">
            <div className="font-display font-extrabold text-sm">Bienvenue, Hassan T.</div>
            <div className="text-[10px] text-cream-200">Directeur Général · Année 2026 / 2027</div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <KpiCard label="Étudiants inscrits" value="1 247" delta="+ 12% vs N-1" />
            <KpiCard label="Recouvrement" value="87" valueSuffix="%" delta="+ 4 pts ce mois" />
            <KpiCard label="Encaissements" value="2,8M" valueSuffix=" DH" delta="+ 8% vs N-1" />
            <KpiCard label="Taux présence" value="94" valueSuffix="%" delta="+ 3 pts" />
          </div>
          <div className="rounded-lg border border-cream-200 p-2.5 mb-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-semibold text-ink-700">Inscriptions par filière</div>
              <div className="text-[8px] text-ink-500">12 mois</div>
            </div>
            <div className="flex items-end gap-1 h-12">
              <div className="flex-1 rounded-t bg-forest-200" style={{ height: '42%' }} />
              <div className="flex-1 rounded-t bg-forest-500" style={{ height: '62%' }} />
              <div className="flex-1 rounded-t bg-forest-500" style={{ height: '55%' }} />
              <div className="flex-1 rounded-t bg-forest-600" style={{ height: '78%' }} />
              <div className="flex-1 rounded-t bg-forest-600" style={{ height: '90%' }} />
              <div className="flex-1 rounded-t bg-forest-700" style={{ height: '70%' }} />
              <div className="flex-1 rounded-t bg-clay-500" style={{ height: '100%' }} />
            </div>
          </div>
          <div className="rounded-lg border border-cream-200 p-2 flex items-center gap-2.5">
            <div className="w-6 h-6 rounded bg-forest-100 flex items-center justify-center text-forest-700 text-[10px] font-bold">EDEP</div>
            <div className="flex-1">
              <div className="text-[10px] font-semibold text-ink-800">Export E-DEP validé</div>
              <div className="text-[9px] text-ink-500">Il y a 2 minutes, 1 247 dossiers</div>
            </div>
            <span className="text-[9px] font-bold text-forest-700 bg-forest-50 px-1.5 py-0.5 rounded">Conforme</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const KpiCard: React.FC<{ label: string; value: string; valueSuffix?: string; delta: string }> = ({ label, value, valueSuffix, delta }) => (
  <div className="rounded-lg border border-cream-200 p-2.5 bg-cream-50">
    <div className="text-[9px] uppercase tracking-wide text-ink-500 font-semibold">{label}</div>
    <div className="font-display font-extrabold text-lg text-ink-800 mt-0.5">{value}{valueSuffix && <span className="text-xs">{valueSuffix}</span>}</div>
    <div className="text-[9px] text-forest-600 font-semibold">{delta}</div>
  </div>
);

const ModuleCard: React.FC<{ label: string; title: string; description: string; features: string[] }> = ({ label, title, description, features }) => (
  <div className="bg-white rounded-3xl border border-cream-300 p-10 lg:p-14 shadow-sm">
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <div className="text-xs uppercase tracking-[0.2em] text-clay-600 font-semibold mb-4">{label}</div>
        <h3 className="font-display font-extrabold text-3xl lg:text-4xl text-ink-800 leading-tight mb-4 tracking-tight">{title}</h3>
        <p className="text-ink-600 leading-relaxed mb-6">{description}</p>
        <a href="#demo" className="text-forest-700 font-semibold underline underline-offset-4 decoration-2 hover:opacity-70 transition">
          Voir une démonstration
        </a>
      </div>
      <div className="lg:col-span-8">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-ink-700">
          {features.map((f, i) => (
            <div key={i} className="flex items-start gap-2"><span className="text-clay-600 mt-1">▸</span><div>{f}</div></div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const SimStat: React.FC<{ label: string; value: string; unit: string; sub: string; accent?: boolean }> = ({ label, value, unit, sub, accent }) => (
  <div className="bg-cream-50 rounded-xl p-5 border border-cream-200">
    <div className="text-xs uppercase tracking-wide text-ink-500 font-semibold mb-1">{label}</div>
    <div className={`font-display font-extrabold text-3xl tracking-tight ${accent ? 'text-clay-600' : 'text-ink-800'}`}>{value} <span className="text-base text-ink-500 font-medium">{unit}</span></div>
    <div className="text-xs text-ink-500 mt-1">{sub}</div>
  </div>
);

const Step: React.FC<{ n: string; title: string; body: string }> = ({ n, title, body }) => (
  <div className="bg-white rounded-2xl border border-cream-300 p-7 flex gap-5">
    <div className="flex-shrink-0 w-10 h-10 rounded-md bg-forest-700 text-white font-display font-extrabold flex items-center justify-center">{n}</div>
    <div>
      <h3 className="font-display font-bold text-ink-800 text-xl mb-2 tracking-tight">{title}</h3>
      <p className="text-ink-600 leading-relaxed">{body}</p>
    </div>
  </div>
);

const FaqItem: React.FC<{ q: string; children: React.ReactNode }> = ({ q, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-cream-300 p-6 hover:border-cream-400 transition">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full font-display font-bold text-ink-800 text-lg flex items-center justify-between cursor-pointer text-left tracking-tight"
      >
        <span>{q}</span>
        <span className={`text-forest-700 transition-transform text-2xl leading-none ${open ? 'rotate-45' : ''}`}>+</span>
      </button>
      {open && <div className="mt-4 text-ink-600 leading-relaxed">{children}</div>}
    </div>
  );
};

const Field: React.FC<{ id: string; label: string; required?: boolean; type?: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; placeholder?: string }> = ({ id, label, required, type = 'text', value, onChange, placeholder }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-semibold mb-1.5 text-cream-200">{label}</label>
    <input
      id={id}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-md bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-white/50 focus:bg-white/10 transition"
    />
  </div>
);

const SelectField: React.FC<{ id: string; label: string; required?: boolean; value: string; onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; children: React.ReactNode }> = ({ id, label, required, value, onChange, children }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-semibold mb-1.5 text-cream-200">{label}</label>
    <select
      id={id}
      required={required}
      value={value}
      onChange={onChange}
      className="w-full px-4 py-3 rounded-md bg-white/5 border border-white/15 text-white focus:outline-none focus:border-white/50 focus:bg-white/10 transition"
    >
      {children}
    </select>
  </div>
);

export default HomePage;
